import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  Typography,
  Chip,
  alpha,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axios from '../../../utils/axios';

const CompanySelectionSection = ({ userData, setUserData, subdomain }) => {
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  console.log(subdomain)

  // Whitelabel org config cached at login — tells us who vets signups
  let orgApprovesJoiners = false;
  let orgApprovesNewCompanies = false;
  try {
    const campusInfo = JSON.parse(localStorage.getItem('campusInfo') || '{}');
    orgApprovesJoiners = !!campusInfo.member_join_approval;
    orgApprovesNewCompanies = orgApprovesJoiners && campusInfo.auto_approve_new_companies === false;
  } catch (e) { /* ignore */ }

  const fetchCompanies = async (search) => {
    if (!search || search.length < 2) {
      setCompanies([]);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`/api/v1/accounts/user/list/companies/?slug=${subdomain}&search=${search}`);
      
      // Add existing companies to the list
      const existingCompanies = response.data.results.map(company => ({
        ...company,
        isExisting: true,
        displayName: company.name,
      }));

      // Always add the "Create new company" option at the bottom if there's search text
      const allOptions = [
        ...existingCompanies,
        {
          id: 'new-company',
          name: search,
          displayName: `Create new company "${search}"`,
          isExisting: false,
        }
      ];

      setCompanies(allOptions);
    } catch (error) {
      console.error('Error fetching companies:', error);
      // Even on error, show the create new option
      setCompanies([{
        id: 'new-company',
        name: search,
        displayName: `Create new company "${search}"`,
        isExisting: false,
      }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchCompanies(searchTerm);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const handleCompanySelect = (event, newValue) => {
    if (!newValue) {
      setUserData({ ...userData, company_name: '', company_id: null });
      return;
    }

    if (newValue.isExisting) {
      // Existing company selected
      setUserData({
        ...userData,
        company_name: newValue.name,
        company_id: newValue.id
      });
    } else {
      // New company to be created
      setUserData({
        ...userData,
        company_name: newValue.name,
        company_id: null
      });
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Autocomplete
        fullWidth
        options={companies}
        loading={loading}
        value={companies.find(c => 
          (userData.company_id && c.id === userData.company_id) || 
          (!userData.company_id && userData.company_name && c.name === userData.company_name && !c.isExisting)
        ) || null}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
          setSearchTerm(newInputValue);
        }}
        onChange={handleCompanySelect}
        getOptionLabel={(option) => option.isExisting ? option.name : option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderOption={(props, option) => (
          <li {...props}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', py: 0.5 }}>
              <Box sx={{ mr: 2, mt: 0.5 }}>
                {option.isExisting ? (
                  <BusinessIcon sx={{ color: 'primary.main' }} />
                ) : (
                  <AddBusinessIcon sx={{ color: 'success.main' }} />
                )}
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: option.isExisting ? 400 : 500 }}>
                  {option.displayName}
                </Typography>
                {option.isExisting && (
                  <>
                    {option.campus?.name && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Campus: {option.campus.name}
                      </Typography>
                    )}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 0.5, 
                      mt: 0.5,
                      p: 1,
                      bgcolor: (theme) => alpha(theme.palette.info.main, 0.08),
                      borderRadius: 1,
                    }}>
                      <InfoOutlinedIcon sx={{ fontSize: 16, color: 'info.main' }} />
                      <Typography variant="caption" sx={{ color: 'info.dark' }}>
                        {orgApprovesJoiners
                          ? "You'll be added to this company once the workspace team approves your request"
                          : "You'll be added to this company pending admin approval"}
                      </Typography>
                    </Box>
                  </>
                )}
                {!option.isExisting && (
                  <Typography variant="caption" color="success.dark">
                    {orgApprovesNewCompanies
                      ? 'This will create a new company account, pending approval by the workspace team'
                      : 'This will create a new company account'}
                  </Typography>
                )}
              </Box>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Company"
            placeholder="Start typing your company name..."
            variant="outlined"
            helperText="Search for your company or create a new one"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <BusinessIcon sx={{ color: 'action.active', mr: 1 }} />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        noOptionsText={
          searchTerm.length < 2 
            ? "Type at least 2 characters to search" 
            : "No companies found. Select 'Create new company' option above"
        }
        sx={{
          '& .MuiAutocomplete-option': {
            '&:hover': {
              bgcolor: 'action.hover',
            },
          },
        }}
      />
      
      {/* Show selected company status */}
      {userData.company_name && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Selected:
          </Typography>
          <Chip
            label={userData.company_name}
            size="small"
            color={userData.company_id ? "primary" : "success"}
            icon={userData.company_id ? <BusinessIcon /> : <AddBusinessIcon />}
            sx={{ fontWeight: 500 }}
          />
          {userData.company_id && (
            <Typography variant="caption" color="text.secondary">
              (Existing company)
            </Typography>
          )}
          {!userData.company_id && (
            <Typography variant="caption" color="success.dark">
              (New company)
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CompanySelectionSection;